import {
  getCoreOrMicroserviceFaultErrorMessage as getFaultMessageFallback,
  getFaultContextValues,
  getUnknownFaultErrorMessage as getUnknownFaultFallback
} from '../api/api.fault.helpers';
import {
  ApiFault,
  ApiFaultOrWarnings,
  MicroserviceFault
} from '../api/api.fault.interfaces';
import { isLocalDevelopmentEnvironment } from '../appSettings/app.environment';
import { HttpResponse } from '../interfaces';
import { DebugManager } from '../utility/DebugManager';
import { ErrorConverter } from './ErrorConverter';
import {
  CapturedApiError,
  CapturedError,
  ErrorHandlerConfig,
  ErrorHandlerUserContext,
  ErrorHandlingMode,
  ErrorHandlingOptions,
  ErrorHandlingResult,
  ErrorMiddleware,
  RuntimeErrorContext,
  RuntimeErrorMetadata
} from './errorHandling.types';
import { ErrorLogger } from './ErrorLogger';

/**
 * Singleton error handler that consolidates error routing logic from
 * useApiDispatch and useErrorHandler into a single pipeline.
 *
 * Pipeline: middleware → translate → route (toast/boundary/unhandled) → dedup → LogMessage.
 *
 * Supports per-scope configuration for features like the unified order flow,
 * with scope-specific DebugManager instances for local debugging.
 */
export class ErrorHandler {
  private static instance: ErrorHandler | undefined;

  private debugManager?: DebugManager;

  private middleware: ErrorMiddleware[] = [];

  private scopeDebugManagers: Map<string, DebugManager> = new Map();

  private logger: ErrorLogger;

  private showToast?: ErrorHandlerConfig['showToast'];

  private triggerServerErrorEvent?: ErrorHandlerConfig['triggerServerErrorEvent'];

  private submitLog?: ErrorHandlerConfig['submitLog'];

  private applicationContext?: ErrorHandlerConfig['applicationContext'];

  private userContext?: ErrorHandlerUserContext;

  private translateFn?: ErrorHandlerConfig['translateFn'];

  private translationExistsFn?: (key: string) => boolean;

  private static readonly UNKNOWN_FAULT_CODE = '0';

  constructor(config?: ErrorHandlerConfig) {
    this.logger = new ErrorLogger(config?.deduplicationTTL);

    if (config) {
      this.applyConfig(config);
    }
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /** Reset the singleton (for testing only). */
  static resetInstance(): void {
    ErrorHandler.instance = undefined;
  }

  /**
   * Configure the ErrorHandler with runtime dependencies.
   * Can be called multiple times — later calls merge with existing config.
   */
  configure(config: ErrorHandlerConfig): void {
    this.applyConfig(config);
  }

  /** Check whether the handler has the minimum dependencies to submit errors to LogMessage. */
  isConfigured(): boolean {
    return !!(this.submitLog && this.applicationContext);
  }

  /**
   * Register a scope-specific DebugManager instance. When errors are
   * handled with `{ scope: 'myScope' }`, the registered DebugManager
   * receives the log entries instead of the global one.
   *
   * @example
   * ErrorHandler.getInstance().registerScopeDebugManager('orderFlow', orderFlowDebug);
   */
  registerScopeDebugManager(scope: string, debugManager: DebugManager): void {
    this.scopeDebugManagers.set(scope, debugManager);
  }

  /** Register a middleware function in the processing pipeline. */
  use(middleware: ErrorMiddleware): void {
    this.middleware.push(middleware);
  }

  setApplicationContext(ctx: ErrorHandlerConfig['applicationContext']): void {
    this.applicationContext = ctx;
  }

  setUserContext(ctx: ErrorHandlerUserContext): void {
    this.userContext = ctx;
  }

  /**
   * Main API error handling entry point.
   *
   * Pipeline: middleware → translate → route → dedup → submit.
   * Executes toast/boundary routing and LogMessage submission.
   */
  handleApiError(
    error: CapturedApiError,
    options: ErrorHandlingOptions = {}
  ): ErrorHandlingResult {
    const debugMgr = this.getEffectiveDebugManager(options.scope);

    const processedError = this.runMiddleware({
      ...error
    }) as CapturedApiError;

    const translatedMessage = this.translateErrorMessage(
      processedError,
      options
    );

    const routingMode: ErrorHandlingMode = options.errorHandling || 'toast';

    const customFaultMatch = this.checkCustomFaultHandler(
      processedError,
      options
    );

    const isDuplicate = this.logger.isDuplicate(processedError);

    const shouldTriggerEvent =
      !translatedMessage && routingMode === 'toast' && !customFaultMatch;

    processedError.userContext.translatedMessage = translatedMessage;

    switch (routingMode) {
      case 'errorBoundary': {
        processedError.isHandled = true;
        break;
      }
      case 'unhandled': {
        break;
      }
      case 'toast':
      default: {
        processedError.isHandled = true;

        if (customFaultMatch) {
          options.customFaultHandler?.({ capturedError: processedError });
        } else if (shouldTriggerEvent) {
          this.triggerServerErrorEvent?.(processedError);
        } else if (translatedMessage) {
          this.showToast?.(translatedMessage);
        }
        break;
      }
    }

    if (!isDuplicate) {
      this.submitToLogService(processedError);
    }

    debugMgr?.log('api', `Error handled: ${routingMode}`, {
      translatedMessage,
      deduplicated: isDuplicate,
      subtype: error.subtype
    });

    return {
      mode: routingMode,
      translatedMessage,
      shouldTriggerBoundary: routingMode === 'errorBoundary',
      capturedError: processedError,
      deduplicated: isDuplicate
    };
  }

  /**
   * Handle a runtime (non-API) error, e.g. from a React error boundary.
   * Converts the raw Error to a CapturedRuntimeError and submits it.
   */
  handleRuntimeError(error: Error, context?: RuntimeErrorContext): void {
    const debugMgr = this.getEffectiveDebugManager(context?.domain);
    const captured = ErrorConverter.fromRuntimeError(error);

    if (context) {
      const metadata: RuntimeErrorMetadata = {
        domain: context.domain,
        tier: context.tier,
        instanceLabel: context.instanceLabel,
        componentStack: context.componentStack
      };
      const capturedWithMetadata = captured as CapturedError & {
        metadata: RuntimeErrorMetadata;
      };
      capturedWithMetadata.metadata = metadata;
    }

    this.submitToLogService(captured);

    debugMgr?.log('api', `Runtime error handled`, {
      message: error.message,
      domain: context?.domain,
      tier: context?.tier
    });
  }

  /**
   * Handle an unhandled promise rejection event.
   * Converts the event via ErrorConverter and submits to LogMessage.
   */
  handleUnhandledRejection(event: PromiseRejectionEvent): void {
    event.preventDefault();

    const captured = ErrorConverter.fromUnhandledRejection(event);
    const isDuplicate = this.logger.isDuplicate(captured);

    if (!isDuplicate) {
      this.submitToLogService(captured);
    }

    this.debugManager?.log('api', 'Unhandled rejection captured', {
      message: captured.message,
      alreadyCaptured: !!event.reason?.isCaptured,
      deduplicated: isDuplicate
    });
  }

  /**
   * Translate an API error into a user-facing message.
   *
   * Priority chain:
   * 1. Custom translateMessage function (highest priority)
   * 2. Hook faultMessages map, interpolated with Fault.ContextData
   * 3. Core fault / Microservice fault -> translated via DI translateFn
   * 4. Error with errorContext.code -> unknown fault message via DI translateFn
   * 5. No match -> returns empty string (caller decides: event or silent)
   */
  translateErrorMessage(
    error: CapturedApiError,
    options: ErrorHandlingOptions = {}
  ): string {
    const customMessage = options.translateMessage?.(error);
    if (customMessage) return customMessage;

    const responseData = error.applicationContext?.apiMessage?.response
      ?.data as Record<string, any> | undefined;
    if (responseData?.faultMessages) {
      const fault = responseData.Fault;
      const faultCode = fault ? ErrorConverter.getFaultCode(fault) : '';
      const serverMessage = faultCode
        ? responseData.faultMessages[faultCode]
        : undefined;
      if (serverMessage) {
        return this.translateFn
          ? this.translateFn(serverMessage, getFaultContextValues(fault))
          : serverMessage;
      }
    }

    if (
      ErrorConverter.isCapturedApiErrorCoreFaultError(error) ||
      ErrorConverter.isCapturedErrorMicroserviceFaultError(error)
    ) {
      return this.translateFn
        ? this.getCoreOrMicroserviceFaultErrorMessage(
            error.applicationContext.apiMessage.response!
          )
        : getFaultMessageFallback(
            error.applicationContext.apiMessage.response!
          ) || '';
    }

    if (error.errorContext?.code !== undefined) {
      return this.translateFn
        ? this.getUnknownFaultErrorMessage()
        : getUnknownFaultFallback() || '';
    }

    return '';
  }

  private getUnknownFaultErrorMessage(): string {
    return this.translateFn?.(ErrorHandler.UNKNOWN_FAULT_CODE) || '';
  }

  private getFaultErrorMessage(fault: ApiFault): string {
    const code = `${fault?.Code || ''}`;
    const codeAndSubcode = ErrorConverter.getFaultCode(fault);
    const contextValues = getFaultContextValues(fault);

    if (codeAndSubcode && this.translationExistsFn?.(codeAndSubcode)) {
      return this.translateFn!(codeAndSubcode, contextValues);
    }

    if (code && this.translationExistsFn?.(code)) {
      return this.translateFn!(code, contextValues);
    }

    const unknownMessage =
      this.translateFn?.(ErrorHandler.UNKNOWN_FAULT_CODE) || '';
    return `${unknownMessage}${codeAndSubcode ? ` - ${codeAndSubcode}` : ''}`;
  }

  private getCoreOrMicroserviceFaultErrorMessage(
    errorResponse: HttpResponse<ApiFaultOrWarnings | MicroserviceFault>
  ): string {
    const isApiFault = Number.isInteger(
      (errorResponse.data as ApiFaultOrWarnings)?.Fault?.Code
    );
    return this.getFaultErrorMessage(
      isApiFault
        ? (errorResponse.data as ApiFaultOrWarnings)?.Fault
        : {
            Code: (errorResponse.data as MicroserviceFault).Code.Value,
            Message: (errorResponse.data as MicroserviceFault).Detail
          }
    );
  }

  /**
   * Submit a captured error to the LogMessage API.
   * Gracefully handles the unconfigured state by falling back to console logging.
   */
  submitToLogService(error: CapturedError): Promise<void> {
    if (!this.isConfigured()) {
      this.debugManager?.log(
        'api',
        `Error not submitted — ErrorHandler not fully configured (missing submitLog or applicationContext)`,
        { message: error.message, subtype: error.subtype }
      );
      if (isLocalDevelopmentEnvironment()) {
        /* eslint-disable no-console */
        console.warn(
          'ErrorHandler: not fully configured. Error not submitted:',
          error.message
        );
        /* eslint-enable no-console */
      }
      return Promise.resolve();
    }

    return this.logger.submit({
      capturedError: error,
      apiFunc: this.submitLog!,
      applicationContext: this.applicationContext!,
      userContext: this.userContext || { displayLanguage: '', language: '' }
    });
  }

  /** Access the logger instance (for testing). */
  getLogger(): ErrorLogger {
    return this.logger;
  }

  private applyConfig(config: ErrorHandlerConfig): void {
    if (config.debugManager !== undefined) {
      this.debugManager = config.debugManager;
    }
    if (config.showToast !== undefined) this.showToast = config.showToast;
    if (config.triggerServerErrorEvent !== undefined) {
      this.triggerServerErrorEvent = config.triggerServerErrorEvent;
    }
    if (config.submitLog !== undefined) this.submitLog = config.submitLog;
    if (config.applicationContext !== undefined) {
      this.applicationContext = config.applicationContext;
    }
    if (config.userContext !== undefined) {
      this.userContext = config.userContext;
    }
    if (config.translateFn !== undefined) {
      this.translateFn = config.translateFn;
    }
    if (config.translationExistsFn !== undefined) {
      this.translationExistsFn = config.translationExistsFn;
    }
  }

  /**
   * Run the middleware pipeline. Each middleware can inspect/modify the error
   * before it reaches the routing logic. Middleware that doesn't call next()
   * short-circuits the pipeline.
   */
  private runMiddleware(error: CapturedError): CapturedError {
    if (this.middleware.length === 0) return error;

    let currentError = error;
    let index = 0;

    const next = (err: CapturedError) => {
      currentError = err;
      index++;
      if (index < this.middleware.length) {
        this.middleware[index](currentError, next);
      }
    };

    this.middleware[0](currentError, next);
    return currentError;
  }

  /**
   * Check if the error matches any custom-handled fault codes.
   * Returns true if a match is found (caller should invoke customFaultHandler).
   */
  private checkCustomFaultHandler(
    error: CapturedApiError,
    options: ErrorHandlingOptions
  ): boolean {
    if (!options.customHandledFaultCodes || !options.customFaultHandler) {
      return false;
    }

    const apiResponse = error.applicationContext?.apiMessage?.response;
    const faultData = apiResponse?.data as ApiFaultOrWarnings | undefined;

    return !!options.customHandledFaultCodes.find(
      (fault) =>
        fault.code === faultData?.Fault?.Code &&
        fault.subCode === faultData?.Fault?.Subcode
    );
  }

  private getEffectiveDebugManager(scope?: string): DebugManager | undefined {
    if (scope) {
      const scopeDebugMgr = this.scopeDebugManagers.get(scope);
      if (scopeDebugMgr) return scopeDebugMgr;
    }
    return this.debugManager;
  }
}
